import React, {useCallback} from 'react'
import PropTypes from 'prop-types'
import ProductTile, {
    Skeleton as ProductTileSkeleton
} from '../../../components/product-tile/index.jsx'
import {useWishlistActions} from '@app/hooks/use-wishlist-actions'
import useEinstein from '../../../hooks/use-einstein.js'

/**
 * PDCEditoProductTile is a React functional component that renders a product tile with various interactive features.
 * It integrates with the Einstein recommendation system and manages wishlist functionality.
 *
 * @param {Object} props - The properties object.
 * @param {string} props.productId - The unique identifier for the product to be displayed.
 * @param {string} props.imageType - The type of image to be used for the product tile.
 * @param {Array} props.productList - An array of product objects from which the specific product is selected.
 * @param {string} props.category - The category of the product, used for tracking clicks with Einstein.
 * @param {Array} props.imageSizes - An optional array of image sizes for responsive design.
 *
 * @returns {JSX.Element|null} A JSX element representing the product tile, or null if the product is not found.
 *
 * The component utilizes several hooks:
 * - `useEinstein`: To send click events to the Einstein recommendation system.
 * - `useProductListWishlist`: To manage the wishlist state, including adding and removing items.
 * - `useCallback`: To memoize the click handler for product interactions.
 *
 * The rendered `ProductTile` component includes:
 * - A click handler that sends category click events to Einstein.
 * - Wishlist toggle functionality, allowing users to add or remove the product from their wishlist.
 * - Dynamic image properties for responsive design.
 */
export const PDCEditoProductTile = ({
    productId,
    imageType,
    productList,
    category,
    imageSizes,
    layoutIndex
}) => {
    const einstein = useEinstein()
    const product = productList?.find((product) => product.productId === productId)
    const {wishlist, addProductToWishlist, removeProductFromWishlist} = useWishlistActions()
    const isInWishlist = !!wishlist?.customerProductListItems?.find(
        (item) => item?.productId === productId
    )
    const memoizedHandleProductClick = useCallback((productSearchItem) => {
        if (category) {
            einstein.sendClickCategory(category, productSearchItem)
        }
    }, [])

    return product ? (
        <ProductTile
            className="product-tile"
            data-testid={`sf-product-tile-${product}`}
            product={product}
            isFavourite={isInWishlist}
            isFavIconInTitle={true}
            onClick={() => memoizedHandleProductClick(product)}
            onFavouriteToggle={(toBeFavourite) => {
                const action = toBeFavourite ? addProductToWishlist : removeProductFromWishlist
                return action(product)
            }}
            dynamicImageProps={{
                widths: imageSizes || ['384px', '384px', '400px', '360px', '480px', '505px']
            }}
            mainImageType={imageType}
            hoverImageType={imageType !== 'front' ? 'front' : null}
            isImageLazyLoading={layoutIndex > 0}
        />
    ) : (
        <ProductTileSkeleton />
    )
}

PDCEditoProductTile.propTypes = {
    productId: PropTypes.string.isRequired,
    imageType: PropTypes.string,
    displayPriority: PropTypes.bool,
    productList: PropTypes.array,
    category: PropTypes.object,
    imageSizes: PropTypes.oneOfType([PropTypes.array, PropTypes.string, PropTypes.object]),
    layoutIndex: PropTypes.number
}
